import {
  TGetDoorCostReportQueryParams,
  TGetDoorCostReportQueryResponse,
} from 'features/summary-report/suppliers/door-cost/types';
import {
  TAddEditDoorCostDetailPayload,
  TAddEditDoorCostExtraInfoPayload,
  TDoorCostGeneralInfo,
  TGetDoorCostDetailQueryResponse,
  TGetDoorCostQueryParams,
  TImportDoorCostDataResponse,
} from 'features/suppliers-management/door-cost/types';
import { baseApi } from 'lib/rtkQuery/baseApi';
import { QueryTags } from 'lib/rtkQuery/constants';
import { invalidatesTags } from 'lib/rtkQuery/helper';
import { ListItemQueryResponse, QueryResponse } from 'lib/rtkQuery/types';

const doorCostApiEndpoint = '/door-costs';
const doorCostDetailApiEndpoint = '/door-cost-details';

export const doorCostApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [QueryTags.DoorCostDetail, QueryTags.DoorCostList],
  })
  .injectEndpoints({
    endpoints: builder => ({
      GetAllDoorCosts: builder.query<
        ListItemQueryResponse<TDoorCostGeneralInfo[]>,
        TGetDoorCostQueryParams
      >({
        query: params => ({
          url: `${doorCostApiEndpoint}`,
          params: { ...params, is_active: true },
        }),
        providesTags: [QueryTags.DoorCostList],
      }),

      GetDoorCostDetail: builder.query<
        QueryResponse<TGetDoorCostDetailQueryResponse>,
        string
      >({
        query: id => ({
          url: `${doorCostApiEndpoint}/${id}`,
        }),
        providesTags: [QueryTags.DoorCostDetail],
      }),

      GetAllDoorCostsReport: builder.query<
        TGetDoorCostReportQueryResponse,
        TGetDoorCostReportQueryParams
      >({
        query: params => ({
          url: `${doorCostApiEndpoint}/report`,
          params: { ...params, is_active: true },
        }),
      }),

      GetAllDoorCostDetailsReport: builder.query<
        TGetDoorCostReportQueryResponse,
        TGetDoorCostReportQueryParams
      >({
        query: params => ({
          url: `${doorCostDetailApiEndpoint}/report`,
          params: { ...params, is_active: true },
        }),
      }),

      ImportDoorCost: builder.mutation<
        QueryResponse<TImportDoorCostDataResponse>,
        FormData
      >({
        query: formData => ({
          url: `${doorCostApiEndpoint}/import`,
          method: 'POST',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }),
      }),

      CreateNewDoorCost: builder.mutation<
        QueryResponse<TDoorCostGeneralInfo>,
        TAddEditDoorCostDetailPayload['data']
      >({
        query: data => ({
          url: `${doorCostApiEndpoint}`,
          method: 'POST',
          data,
        }),
      }),

      UpdateDoorCostDetail: builder.mutation<
        QueryResponse<TDoorCostGeneralInfo>,
        TAddEditDoorCostDetailPayload
      >({
        query: ({ id, data }) => ({
          url: `${doorCostApiEndpoint}/${id}`,
          method: 'PUT',
          data,
        }),
        invalidatesTags: invalidatesTags([
          QueryTags.DoorCostDetail,
          QueryTags.DoorCostList,
        ]),
      }),

      DeleteDoorCost: builder.mutation<any, string>({
        query: id => ({
          url: `${doorCostApiEndpoint}/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: invalidatesTags([QueryTags.DoorCostList]),
      }),

      DeleteDoorCostDetail: builder.mutation<any, string>({
        query: id => ({
          url: `${doorCostDetailApiEndpoint}/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: invalidatesTags([QueryTags.DoorCostDetail]),
      }),

      CreateNewDoorCostExtraDetail: builder.mutation<
        QueryResponse<any>,
        TAddEditDoorCostExtraInfoPayload['data']
      >({
        query: data => ({
          url: `${doorCostDetailApiEndpoint}`,
          method: 'POST',
          data,
        }),
        invalidatesTags: invalidatesTags([QueryTags.DoorCostDetail]),
      }),

      UpdateDoorCostExtraDetail: builder.mutation<
        QueryResponse<any>,
        TAddEditDoorCostExtraInfoPayload
      >({
        query: ({ id, data }) => ({
          url: `${doorCostDetailApiEndpoint}/${id}`,
          method: 'PUT',
          data,
        }),
        invalidatesTags: invalidatesTags([QueryTags.DoorCostDetail]),
      }),
    }),
  });

export const {
  useGetAllDoorCostsQuery,
  useGetAllDoorCostsReportQuery,
  useGetAllDoorCostDetailsReportQuery,
  useGetDoorCostDetailQuery,
  useImportDoorCostMutation,
  useUpdateDoorCostDetailMutation,
  useCreateNewDoorCostMutation,
  useCreateNewDoorCostExtraDetailMutation,
  useUpdateDoorCostExtraDetailMutation,
  useDeleteDoorCostMutation,
  useDeleteDoorCostDetailMutation,
} = doorCostApi;
